/**
 * Batch Scheduler Types
 * Types for parallel job execution with rate limiting
 */

import type { TranslationFile } from '../../types';

export type JobType = 'translation' | 'semantic-match' | 'audit';
export type JobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
export type PlanStatus = 'pending' | 'planning' | 'ready' | 'running' | 'paused' | 'completed' | 'cancelled';

export interface JobEstimate {
    fileId: string;
    fileName: string;
    estimatedTokens: number;
    estimatedRequests: number; // 3 for full pipeline (trans + match + audit)
    priority: number;
    file: TranslationFile;
}

export interface ScheduledJob {
    id: string;
    fileId: string;
    fileName: string;
    type: JobType;
    status: JobStatus;
    estimatedTokens: number;
    actualTokens?: number;
    startTime?: number;
    endTime?: number;
    error?: string;
    progress?: number; // 0-100
}

export interface ExecutionPlan {
    id: string;
    jobs: ScheduledJob[]; // Flat list of all jobs, sorted by execution order
    totalFiles: number;
    totalJobs: number;
    estimatedTokens: number;
    estimatedTime: number; // in seconds
    estimatedCost: number;
    status: PlanStatus;
    createdAt: number;
}

export interface SchedulerProgress {
    planId: string;
    completedJobs: number;
    totalJobs: number;
    runningJobs: number;
    failedJobs: number;
    elapsedTime: number;
    estimatedRemainingTime: number;
    currentThroughputTpm: number; // Tokens per minute
}

export interface SchedulerConfig {
    maxConcurrent: number;
    autoRetry: boolean;
    maxRetries: number;
}

export const DEFAULT_SCHEDULER_CONFIG: SchedulerConfig = {
    maxConcurrent: 50, // Increased from 5 to 50 to maximize throughput within token limits
    autoRetry: true,
    maxRetries: 2
};
